<script setup lang="ts">
    import Decimal from 'decimal.js';
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';
    const emit = defineEmits<{
        (e: 'blur', value: number | number): void;
        (e: 'change', value: number | number): void;
    }>();

    const props = withDefaults(
        defineProps<
            FormInputProps & {
                min?: number;
                max?: number;
                step?: number;
                minimumFractionDigits?: number;
                maximumFractionDigits?: number;
            }
        >(),
        {
            min: 0,
            max: 999999999,
            step: 0.001,
            maximumFractionDigits: 3,
            minimumFractionDigits: 2,
        }
    );
    const inputData: FormInputData = useFormInput(props);
    const euroToCent = (euroValue: string | number): number => {
        const decimal = new Decimal(euroValue);

        return decimal.times(1000).round().div(10).toNumber();
    };

    const centToEuro = (centValue: string | number): number => {
        const decimal = new Decimal(centValue);
        return decimal.times(10).round().div(1000).toDecimalPlaces(props.maximumFractionDigits).toNumber();
    };

    const mask = (centValue: number) => {
        const euroValue = centToEuro(centValue);

        return euroValue.toLocaleString('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: props.minimumFractionDigits,
            maximumFractionDigits: props.maximumFractionDigits,
        });
    };
</script>

<template>
    <FormFormattedNumberInput
        :input-name="inputName"
        :label="label"
        :placeholder="placeholder"
        :form="form"
        :model="model"
        :min="min"
        :max="max"
        :step="step"
        :disabled="inputData.props.disabled"
        :filled-by-ai="filledByAi"
        :size="size"
        :mask="mask"
        :unmask="(value: string | number) => centToEuro(value)"
        :setter="(value: string | number) => euroToCent(value)"
        :select-on-focus="selectOnFocus"
        :without-label="inputData.props.withoutLabel"
        :class-name="inputData.props.className"
        :element-class-name="inputData.props.elementClassName"
        :container-class-name="inputData.props.containerClassName"
        @blur="emit('blur', $event)"
        @change="emit('change', $event)"
    />
</template>
